import { useRef, useEffect, useState, type ReactNode } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import type { SceneSection } from '../manifest';
import { useScene } from '../scene-context';
import { SceneMedia } from '../SceneMedia';
import { Particles } from '../Particles';
import { ParallaxLayer } from '../interactive/ParallaxLayer';
import { TiltCard } from '../interactive/TiltCard';
import { StickyScrollStory } from '../interactive/StickyScrollStory';
import { CountdownFlip } from '../interactive/CountdownFlip';
import { RevealScratch } from '../interactive/RevealScratch';
import { LightDiya } from '../interactive/LightDiya';
import { LiftVeil } from '../interactive/LiftVeil';
import { PetalShower } from '../interactive/PetalShower';
import { Panorama } from '../interactive/Panorama';
import { Portals } from '../interactive/Portals';
import { Living } from '../interactive/Living';
import { Corridor } from '../interactive/Corridor';
import { SuperheroCharacterOverlay } from '../interactive/SuperheroCharacterOverlay';
import { DarkKnightCharacterOverlay } from '../interactive/DarkKnightCharacterOverlay';
import { WonderlandCharacterOverlay } from '../interactive/WonderlandCharacterOverlay';
import { AtmosphericMotionEngine } from '../interactive/AtmosphericMotionEngine';
import { SuperArcBlastEngine } from '../interactive/SuperArcBlastEngine';
import { CandleBlowerWidget } from '../interactive/CandleBlowerWidget';
import { SuperheroBadgeCreator } from '../interactive/SuperheroBadgeCreator';
import { AmbientAudioSynthesizer } from '../interactive/AmbientAudioSynthesizer';
import { Interactive3DCubeCard } from '../interactive/Interactive3DCubeCard';
import { IronManThanosGame } from '../interactive/IronManThanosGame';
import { DecorLayer } from '../decor/DecorLayer';

// Entry animation imports
import { FadeUpEntry } from './entries/FadeUpEntry';
import { ZoomInEntry } from './entries/ZoomInEntry';
import { ColorWashEntry } from './entries/ColorWashEntry';
import { CurtainRevealEntry } from './entries/CurtainRevealEntry';

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

interface RitualSectionProps {
  section: SceneSection;
}

// Foreground content drifts opposite to the background at a fraction of its
// speed — a lightweight second depth "layer" distinct from the single
// background-only parallax already used by `animation.parallax.enabled`.
function ContentParallaxLayer({ children, intensity }: { children: ReactNode; intensity: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [`${intensity * 40}px`, `-${intensity * 40}px`]);

  return (
    <motion.div ref={ref} style={{ y: shouldReduceMotion ? 0 : y }} className="relative z-10 text-center px-6 max-w-2xl w-full">
      {children}
    </motion.div>
  );
}

function getSectionCardStyle(packId: string, colors: { primary: string }) {
  if (packId === 'heritage') {
    return {
      backgroundColor: 'rgba(28,6,8,0.72)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: `1px solid ${colors.primary}55`,
      boxShadow: `0 24px 80px rgba(0,0,0,0.7), inset 0 0 24px ${colors.primary}20`,
      padding: '2.5rem 2rem',
      borderRadius: '2rem',
      maxWidth: '30rem',
      marginLeft: 'auto',
      marginRight: 'auto',
    };
  }
  if (packId === 'pastel_dream') {
    return {
      backgroundColor: 'rgba(31, 11, 24, 0.82)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(249, 168, 212, 0.55)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.75), inset 0 0 24px rgba(249, 168, 212, 0.2)',
      padding: '2.5rem 2rem',
      borderRadius: '2rem',
      maxWidth: '30rem',
      marginLeft: 'auto',
      marginRight: 'auto',
    };
  }
  if (packId === 'modern_noir') {
    return {
      backgroundColor: 'rgba(5, 5, 10, 0.88)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(250, 204, 21, 0.6)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.9), inset 0 0 24px rgba(250, 204, 21, 0.2)',
      padding: '2.5rem 2rem',
      borderRadius: '2rem',
      maxWidth: '30rem',
      marginLeft: 'auto',
      marginRight: 'auto',
    };
  }
  if (packId === 'midnight_glow') {
    return {
      backgroundColor: 'rgba(2, 6, 23, 0.88)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      border: '2px solid rgba(14, 165, 233, 0.6)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.9)',
      padding: '2.5rem 2rem',
      borderRadius: '2rem',
      maxWidth: '30rem',
      marginLeft: 'auto',
      marginRight: 'auto',
    };
  }
  return {
    backgroundColor: 'rgba(10, 10, 15, 0.75)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${colors.primary}55`,
    boxShadow: '0 24px 80px rgba(0,0,0,0.65)',
    padding: '2.5rem 2rem',
    borderRadius: '2rem',
    maxWidth: '30rem',
    marginLeft: 'auto',
    marginRight: 'auto',
  };
}

export function RitualSection({ section }: RitualSectionProps) {
  const { pack } = useScene();
  const isMobile = useIsMobileViewport();
  const sectionRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const [showCard, setShowCard] = useState(true);

  const showParallax = section.animation.parallax.enabled && !prefersReducedMotion;
  const washColor = section.ritualId === 'haldi' ? '#F2B705' : pack.colors.primary;
  const threshold = section.animation.scrollTriggerThreshold;
  const intensity = section.animation.parallax.intensity ?? 0.3;
  const interactiveKind = section.interactive?.kind;

  // Returns the correct entry animation wrapper without defining a component inside render
  function renderWithEntryAnimation(children: ReactNode): ReactNode {
    switch (section.animation.entry) {
      case 'fade_up':
        return <FadeUpEntry threshold={threshold}>{children}</FadeUpEntry>;
      case 'zoom_in':
        return <ZoomInEntry threshold={threshold}>{children}</ZoomInEntry>;
      case 'color_wash':
        return (
          <ColorWashEntry color={washColor} threshold={threshold}>
            {children}
          </ColorWashEntry>
        );
      case 'curtain_reveal':
        return <CurtainRevealEntry threshold={threshold}>{children}</CurtainRevealEntry>;
      default:
        return children;
    }
  }

  const cardStyle = getSectionCardStyle(pack.id, pack.colors);

  const contentBlock = (
    <div className="relative group">
      {/* Floating Art Showcase Mode Toggle Button */}
      <button
        onClick={() => setShowCard(!showCard)}
        className="absolute -top-4 right-4 z-30 flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-black/90 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider text-amber-300 shadow-lg backdrop-blur-md transition-all hover:scale-105 active:scale-95 cursor-pointer pointer-events-auto"
        title="Toggle pure background artwork showcase mode"
      >
        <span>{showCard ? '👁️ VIEW ARTWORK' : '✨ SHOW DETAILS'}</span>
      </button>

      <div
        style={{
          ...cardStyle,
          opacity: showCard ? 1 : 0.05,
          transform: showCard ? 'scale(1)' : 'scale(0.94)',
          transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: showCard ? 'auto' : 'none',
        }}
      >
      <p
        className="text-xs md:text-sm uppercase tracking-widest mb-4 opacity-90 font-bold"
        style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}
      >
        {section.content.date}
        {section.content.time ? ` · ${section.content.time}` : ''}
      </p>
      <h2
        className="mb-4 text-4xl md:text-6xl"
        style={{
          color: '#FFFFFF',
          fontFamily: pack.fonts.display,
          fontWeight: 600,
          textShadow: `0 4px 24px rgba(0,0,0,0.8), 0 0 28px ${pack.colors.primary}66`,
        }}
      >
        {section.content.title}
      </h2>
      {section.content.subtitle && (
        <p
          className="text-xl md:text-3xl mb-6 font-semibold"
          style={{
            color: '#FFFFFF',
            fontFamily: pack.fonts.script ?? pack.fonts.display,
          }}
        >
          {section.content.subtitle}
        </p>
      )}
      {interactiveKind === 'countdown_flip' && (
        <div className="mb-6">
          <CountdownFlip
            targetIso={`${section.content.date}T${section.content.time ?? '00:00'}:00`}
            digitColor="#FFFFFF"
            labelColor={pack.colors.primary}
          />
        </div>
      )}
      <p
        className="text-sm md:text-base opacity-90 font-medium mb-4 max-w-lg mx-auto"
        style={{ color: '#FFFFFF', fontFamily: pack.fonts.body }}
      >
        {section.content.venue?.name}
      </p>
      {section.content.customNote && (
        <div className="mt-6">
          <p
            className="text-xs md:text-sm italic opacity-95 border-t border-b py-2 px-6 inline-block mx-auto uppercase tracking-wider font-bold"
            style={{
              borderColor: `${pack.colors.primary}55`,
              color: pack.colors.primary,
              fontFamily: pack.fonts.body,
            }}
          >
            {section.content.customNote}
          </p>
        </div>
      )}
    </div>
  </div>
  );

  // sticky_story replaces the whole section layout with the pinned-media /
  // scrolling-content split panel — a distinct treatment from the centered
  // hero-style layout used below, so it branches before the shared markup.
  if (interactiveKind === 'sticky_story') {
    return (
      <div data-section-id={section.sectionId} data-entry={section.animation.entry}>
        <StickyScrollStory
          panels={[
            {
              id: section.sectionId,
              assets: section.assets,
              mobileBehavior: section.mobileBehavior,
              content: (
                <div style={{ color: pack.colors.text }}>
                  {contentBlock}
                </div>
              ),
            },
          ]}
        />
      </div>
    );
  }

  // Slow auto-playing drift on the background so every section has visible
  // motion without hover — hover-only effects read as static on touch devices.
  const backgroundMedia = prefersReducedMotion ? (
    <SceneMedia assets={section.assets} mobileBehavior={section.mobileBehavior} />
  ) : (
    <div className="w-full h-full animate-ken-burns-slow">
      <style>{`
        @keyframes ken-burns-slow {
          0% { transform: scale(1.0); }
          100% { transform: scale(1.08); }
        }
        .animate-ken-burns-slow {
          animation: ken-burns-slow 12s ease-in-out infinite alternate;
        }
      `}</style>
      <SceneMedia assets={section.assets} mobileBehavior={section.mobileBehavior} />
    </div>
  );

  const backgroundAndEffectsLayer = (
    <>
      {/* Background Media with Parallax / Scratch-Reveal option */}
      {interactiveKind === 'reveal_scratch' ? (
        <RevealScratch overlayColor={pack.colors.primary} className="absolute inset-0 w-full h-full">
          {backgroundMedia}
        </RevealScratch>
      ) : showParallax ? (
        <ParallaxLayer speed={-intensity} className="absolute inset-0 w-full h-full pointer-events-none">
          {backgroundMedia}
        </ParallaxLayer>
      ) : (
        <div className="absolute inset-0 w-full h-full pointer-events-none">
          {backgroundMedia}
        </div>
      )}

      {/* Vignette Overlay */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: pack.vignette }}
        aria-hidden
      />

      {/* Particles Overlay */}
      <Particles
        effect={section.assets.particleEffect}
        reduce={isMobile ? section.mobileBehavior.reduceParticles : false}
      />
      <DecorLayer decor={section.decor} />

      {/* Dynamic 60fps Atmospheric Motion Engine & Particles */}
      {pack.id === 'midnight_glow' && <AtmosphericMotionEngine theme="superhero" />}
      {pack.id === 'modern_noir' && <AtmosphericMotionEngine theme="dark_knight" />}
      {pack.id === 'pastel_dream' && <AtmosphericMotionEngine theme="wonderland" />}

      {/* Dynamic Theme Character & Interactive Overlays */}
      {section.order === 0 && pack.id === 'midnight_glow' && <SuperheroCharacterOverlay />}
      {section.order === 0 && pack.id === 'modern_noir' && <DarkKnightCharacterOverlay />}
      {pack.id === 'pastel_dream' && <WonderlandCharacterOverlay />}

      {/* Superhero game ONLY for midnight_glow */}
      {section.order === 0 && pack.id === 'midnight_glow' && (
        <IronManThanosGame />
      )}
    </>
  );

  const contentLayer = interactiveKind === 'tilt_card' ? (
    <TiltCard className="relative z-10 text-center px-6 max-w-2xl w-full" intensity={12}>
      {renderWithEntryAnimation(contentBlock)}
    </TiltCard>
  ) : interactiveKind === 'panorama' ? (
    <Panorama imageUrl={section.assets.primaryImage.publicUrl} gyroscope lightShafts>
      <div className="relative z-10 text-center px-6 max-w-2xl w-full">
        {renderWithEntryAnimation(contentBlock)}
      </div>
    </Panorama>
  ) : interactiveKind === 'portals' ? (
    <Portals doorImage={section.assets.primaryImage.publicUrl}>
      <div className="relative z-10 text-center px-6 max-w-2xl w-full">
        {renderWithEntryAnimation(contentBlock)}
      </div>
    </Portals>
  ) : interactiveKind === 'living' ? (
    <Living motionType="fireflies">
      <div className="relative z-10 text-center px-6 max-w-2xl w-full">
        {renderWithEntryAnimation(contentBlock)}
      </div>
    </Living>
  ) : interactiveKind === 'corridor' ? (
    <Corridor>
      <div className="relative z-10 text-center px-6 max-w-2xl w-full">
        {renderWithEntryAnimation(contentBlock)}
      </div>
    </Corridor>
  ) : interactiveKind === 'depth' || interactiveKind === 'parallax_layers' ? (
    <ContentParallaxLayer intensity={intensity}>
      {renderWithEntryAnimation(contentBlock)}
    </ContentParallaxLayer>
  ) : interactiveKind === 'light_diya' ? (
    <LightDiya>{renderWithEntryAnimation(contentBlock)}</LightDiya>
  ) : interactiveKind === 'lift_veil' && section.order === 0 ? (
    <LiftVeil>{renderWithEntryAnimation(contentBlock)}</LiftVeil>
  ) : interactiveKind === 'petal_shower' ? (
    <PetalShower>{renderWithEntryAnimation(contentBlock)}</PetalShower>
  ) : (
    <div className="relative z-10 text-center px-6 max-w-2xl w-full">
      {renderWithEntryAnimation(contentBlock)}
    </div>
  );

  return (
    <section
      ref={sectionRef}
      data-section-id={section.sectionId}
      data-entry={section.animation.entry}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: pack.colors.background }}
    >
      {backgroundAndEffectsLayer}
      {contentLayer}
    </section>
  );
}
